import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  Image as ImageIcon, 
  Video, 
  FileText, 
  Cloud, 
  HardDrive,
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type configurations
const FILE_CONFIGS = {
  image: {
    accept: 'image/jpeg,image/png,image/webp,image/gif',
    maxSize: 10 * 1024 * 1024, // 10MB
    icon: ImageIcon,
    label: 'Görsel',
    endpoint: '/api/upload/image'
  },
  video: {
    accept: 'video/mp4,video/webm,video/quicktime',
    maxSize: 100 * 1024 * 1024, // 100MB
    icon: Video,
    label: 'Video',
    endpoint: '/api/upload/video'
  },
  pdf: {
    accept: 'application/pdf',
    maxSize: 50 * 1024 * 1024, // 50MB
    icon: FileText,
    label: 'PDF',
    endpoint: '/api/upload/pdf'
  }
};

// Main Upload Component
export const MediaUploader = ({
  type = 'image', // 'image', 'video', 'pdf'
  value = '',
  onChange,
  onUploadComplete,
  className = '',
  showPreview = true,
  placeholder = 'Dosya yükle veya URL yapıştır'
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [showStorageChoice, setShowStorageChoice] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [urlInput, setUrlInput] = useState(value || '');
  const fileInputRef = useRef(null);

  // Sync urlInput with value prop
  React.useEffect(() => {
    setUrlInput(value || '');
  }, [value]);

  const config = FILE_CONFIGS[type];
  const Icon = config.icon;

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    if (!file) return;

    // Validate file type - more flexible check
    const allowedTypes = config.accept.split(',').map(t => t.trim());
    const fileType = file.type;
    const isValidType = allowedTypes.some(allowed => {
      if (allowed.endsWith('/*')) {
        // Handle wildcard like image/*
        return fileType.startsWith(allowed.replace('/*', '/'));
      }
      return allowed === fileType;
    });
    
    if (!isValidType) {
      toast.error(`Geçersiz dosya tipi. İzin verilen: ${config.accept}`);
      return;
    }

    // Validate file size
    if (file.size > config.maxSize) {
      toast.error(`Dosya çok büyük. Maksimum: ${Math.round(config.maxSize / 1024 / 1024)}MB`);
      return;
    }

    setPendingFile(file);
    setShowStorageChoice(true);
  }, [config]);

  // Upload file
  const uploadFile = async (storage) => {
    if (!pendingFile) return;

    setShowStorageChoice(false);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', pendingFile);
      formData.append('storage', storage);
      formData.append('folder', `vavwear/${type}s`);

      const response = await fetch(`${API_URL}${config.endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Yükleme başarısız');
      }

      const result = await response.json();
      
      if (result.success) {
        const url = result.url;
        setUrlInput(url);
        onChange?.(url);
        onUploadComplete?.(result);
        toast.success(`${config.label} başarıyla yüklendi (${storage === 'cloudinary' ? 'Cloudinary' : 'Local'})`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Yükleme sırasında hata oluştu');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setPendingFile(null);
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [handleFileSelect]);

  // Handle URL input
  const handleUrlChange = (e) => {
    const url = e.target.value;
    setUrlInput(url);
    onChange?.(url);
  };

  // Clear value
  const handleClear = () => {
    setUrlInput('');
    onChange?.('');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {/* URL Input with Upload Button */}
      <div className="relative flex gap-2">
        <input
          type="text"
          value={urlInput}
          onChange={handleUrlChange}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 bg-vav-bg border border-vav-border rounded-lg text-vav-fg text-sm focus:border-vav-gold focus:outline-none"
        />
        
        {urlInput && (
          <button
            onClick={handleClear}
            className="absolute right-20 top-1/2 -translate-y-1/2 p-1 text-vav-fg-muted hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="px-3 py-2 bg-vav-gold text-black rounded-lg hover:bg-vav-gold/90 transition-colors flex items-center gap-2 text-sm font-medium"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          Yükle
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept={config.accept}
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          className="hidden"
        />
      </div>

      {/* Drag & Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all
          ${dragActive 
            ? 'border-vav-gold bg-vav-gold/10' 
            : 'border-vav-border hover:border-vav-gold/50 hover:bg-vav-bg-subtle'}
        `}
      >
        <Icon className={`w-8 h-8 mx-auto mb-2 ${dragActive ? 'text-vav-gold' : 'text-vav-fg-muted'}`} />
        <p className="text-sm text-vav-fg-muted">
          {dragActive 
            ? 'Dosyayı bırakın...' 
            : `${config.label} sürükle bırak veya tıkla`}
        </p>
        <p className="text-xs text-vav-fg-dark mt-1">
          Maks: {Math.round(config.maxSize / 1024 / 1024)}MB
        </p>
      </div>

      {/* Preview */}
      {showPreview && urlInput && (
        <div className="mt-2">
          {type === 'image' && (
            <img 
              src={urlInput} 
              alt="Preview" 
              className="max-h-32 rounded-lg border border-vav-border"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          {type === 'video' && (
            <video 
              src={urlInput} 
              className="max-h-32 rounded-lg border border-vav-border"
              controls
              muted
            />
          )}
          {type === 'pdf' && (
            <a 
              href={urlInput} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-vav-gold hover:underline text-sm"
            >
              <FileText className="w-4 h-4" />
              PDF Önizle
            </a>
          )}
        </div>
      )}

      {/* Storage Choice Modal */}
      <AnimatePresence>
        {showStorageChoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowStorageChoice(false);
              setPendingFile(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-vav-bg-paper border border-vav-border rounded-xl p-6 w-full max-w-md"
            >
              <h3 className="font-heading text-lg text-vav-fg mb-4 flex items-center gap-2">
                <Icon className="w-5 h-5 text-vav-gold" />
                Depolama Seçin
              </h3>
              
              {pendingFile && (
                <p className="text-sm text-vav-fg-muted mb-4">
                  <span className="font-medium">{pendingFile.name}</span>
                  <span className="ml-2 text-xs">
                    ({(pendingFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Cloudinary Option */}
                <button
                  onClick={() => uploadFile('cloudinary')}
                  className="p-4 border border-vav-border rounded-xl hover:border-vav-gold hover:bg-vav-gold/5 transition-all group"
                >
                  <Cloud className="w-10 h-10 mx-auto mb-2 text-blue-500 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-vav-fg">Cloudinary</p>
                  <p className="text-xs text-vav-fg-muted mt-1">
                    CDN, otomatik optimize
                  </p>
                </button>

                {/* Local Option */}
                <button
                  onClick={() => uploadFile('local')}
                  className="p-4 border border-vav-border rounded-xl hover:border-vav-gold hover:bg-vav-gold/5 transition-all group"
                >
                  <HardDrive className="w-10 h-10 mx-auto mb-2 text-green-500 group-hover:scale-110 transition-transform" />
                  <p className="font-medium text-vav-fg">Sunucu</p>
                  <p className="text-xs text-vav-fg-muted mt-1">
                    Yerel depolama
                  </p>
                </button>
              </div>

              <button
                onClick={() => {
                  setShowStorageChoice(false);
                  setPendingFile(null);
                }}
                className="w-full mt-4 px-4 py-2 text-vav-fg-muted hover:text-vav-fg transition-colors text-sm"
              >
                İptal
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Progress */}
      {isUploading && (
        <div className="mt-2">
          <div className="flex items-center gap-2 text-sm text-vav-fg-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            Yükleniyor...
          </div>
        </div>
      )}
    </div>
  );
};

// Simple Image Input with Upload
export const ImageInput = ({ value, onChange, label, placeholder }) => (
  <div>
    {label && <label className="block text-sm text-vav-fg-muted mb-1">{label}</label>}
    <MediaUploader
      type="image"
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Görsel URL veya yükle'}
    />
  </div>
);

// Simple Video Input with Upload
export const VideoInput = ({ value, onChange, label, placeholder }) => (
  <div>
    {label && (
      <label className="block text-sm text-vav-fg-muted mb-1 flex items-center gap-2">
        <Video className="w-4 h-4 text-vav-gold" />
        {label}
      </label>
    )}
    <MediaUploader
      type="video"
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'Video URL veya yükle (Cloudinary)'}
    />
  </div>
);

// Simple PDF Input with Upload
export const PDFInput = ({ value, onChange, label, placeholder }) => (
  <div>
    {label && (
      <label className="block text-sm text-vav-fg-muted mb-1 flex items-center gap-2">
        <FileText className="w-4 h-4 text-vav-gold" />
        {label}
      </label>
    )}
    <MediaUploader
      type="pdf"
      value={value}
      onChange={onChange}
      placeholder={placeholder || 'PDF URL veya yükle'}
    />
  </div>
);

export default MediaUploader;
